import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { useTimeSignals } from 'modules/notes/hooks/useTimeSignals';
import { useMessage } from 'modules/intl';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { getTimelineEventInfo } from '../util';
import { TIMELINE_PERIOD } from '../timelinePeriod';
import { AppointmentModel, Resource as ReactTimelineResource } from '../types';

const classifications: TimeSignalClassification[] = [
  TimeSignalClassification.Accepted,
  TimeSignalClassification.Private,
  TimeSignalClassification.Trash,
  TimeSignalClassification.Unclassified
];

export const TIMELINE_TIMESIGNAL_COUNT = 249;

export const useTimelineEvents = ({
  period
}: {
  period: TIMELINE_PERIOD;
}): {
  notes: TimeSignal[];
  events: AppointmentModel[];
  reactTimelineResources: ReactTimelineResource[];
  loading: boolean;
  hasAllDayEvent: boolean;
} => {
  const emptyMsg = useMessage('note.emptyNote');
  const { data, loading } = useTimeSignals(TIMELINE_TIMESIGNAL_COUNT);
  const theme = useTheme();
  const notes = useMemo(
    () => (data?.timeSignals?.items as TimeSignal[]) ?? [],
    [data?.timeSignals?.items]
  );
  const events = useMemo<AppointmentModel[]>(
    () =>
      notes.map(
        (note): AppointmentModel => {
          const isMultiDay = (start: DateTime, end: DateTime): boolean => {
            if (!start || !end) return false;
            return start.toISODate() !== end.toISODate();
          };

          const allDay =
            !(note.startTime && note.endTime) ||
            isMultiDay(
              DateTime.fromISO(note.startTime),
              DateTime.fromISO(note.endTime)
            );
          const eventInfo = getTimelineEventInfo(theme, note.classification);
          return {
            id: note.id,
            title:
              note.description && note.description.length
                ? note.description
                : emptyMsg,
            startDate: allDay
              ? DateTime.fromISO(note.date).startOf('day').toISO()
              : DateTime.fromISO(note.startTime).toISO(),
            endDate: allDay
              ? DateTime.fromISO(note.date).endOf('day').toISO()
              : DateTime.fromISO(note.endTime).toISO(),
            type: eventInfo.eventType,
            allDay: allDay,
            note: note,
            period: period,
            color: eventInfo.color
          } as AppointmentModel;
        }
      ),
    [notes, emptyMsg, period, theme]
  );
  events.sort((e1, e2) => {
    if (e1.startDate === e2.startDate) {
      return 0;
    } else if (e1.startDate! < e2.startDate!) {
      return -1;
    } else if (e1.startDate! > e2.startDate!) {
      return 1;
    }
    return 0;
  });

  const hasAllDayEvent = useMemo(() => events.some((event) => event.allDay), [
    events
  ]);

  const reactTimelineResources = useMemo(
    () =>
      classifications.map((type) => {
        const { color, eventType } = getTimelineEventInfo(theme, type);
        return {
          id: eventType,
          text: eventType,
          color: color
        };
      }),
    [theme]
  );

  return {
    events: events,
    loading,
    notes,
    hasAllDayEvent,
    reactTimelineResources
  };
};
